
import { useState } from 'react';
import VideoModal from './VideoModal';

export default function Hero() {
  const [showVideo, setShowVideo] = useState(false);
  const [bedStatus, setBedStatus] = useState<'occupied' | 'pending' | 'available'>('occupied');

  const scrollToDemo = () => {
    const demoSection = document.getElementById('demo-section');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToIntegration = () => {
    const integrationSection = document.getElementById('integration-section');
    if (integrationSection) {
      integrationSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Simulador interactivo de cambio de estado de cama
  const startBedSimulation = () => {
    setBedStatus('occupied');
    setTimeout(() => setBedStatus('pending'), 2000);
    setTimeout(() => setBedStatus('available'), 4000);
    setTimeout(() => setBedStatus('occupied'), 6000);
  };

  useState(() => {
    startBedSimulation();
    const interval = setInterval(startBedSimulation, 7000);
    return () => clearInterval(interval);
  });

  const getBedStatusColor = () => {
    switch (bedStatus) {
      case 'occupied': return 'bg-red-500';
      case 'pending': return 'bg-amber-500';
      case 'available': return 'bg-green-500';
    }
  };

  const getBedStatusText = () => {
    switch (bedStatus) {
      case 'occupied': return 'Ocupada';
      case 'pending': return 'Pendiente limpieza';
      case 'available': return 'Disponible';
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
      {/* Decorative dots pattern */}
      <div className="absolute inset-0 dots-pattern"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-20 pt-28 sm:pt-24">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-5 sm:space-y-8">
            {/* Main Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
              <span className="text-gray-900">Gestión<br className="hidden sm:block" /></span>
              <span className="sm:hidden"> </span>
              <span className="text-gray-900">Hospitalaria<br className="hidden sm:block" /></span>
              <span className="sm:hidden"> </span>
              <span className="text-primary-500">Inteligente</span>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-xl text-gray-600 leading-relaxed">
              Optimiza la ocupación de camas, predice altas médicas y automatiza la comunicación familiar con <strong className="text-primary-500">Inteligencia Artificial</strong>
            </p>

            {/* Features list */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-primary-600 text-sm"></i>
                </div>
                <span className="text-gray-700 text-sm sm:text-base">Sin instalación requerida</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-primary-600 text-sm"></i>
                </div>
                <span className="text-gray-700 text-sm sm:text-base">Integración personalizada</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-primary-600 text-sm"></i>
                </div>
                <span className="text-gray-700 text-sm sm:text-base">Soporte según necesidades</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <button
                onClick={scrollToDemo}
                className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                <span>Solicitar demo</span>
                <i className="ri-arrow-right-line"></i>
              </button>

              <button
                onClick={() => setShowVideo(true)}
                className="btn-secondary flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-play-circle-line"></i>
                <span>Ver vídeo</span>
              </button>
            </div>

            {/* Microcopy mejorado */}
            <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="text-primary-500">✓</span>
                <span>Registro directo</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="text-primary-500">✓</span>
                <span>Sin tarjeta</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="text-primary-500">✓</span>
                <span>Datos demo incluidos</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="text-primary-500">✓</span>
                <span>Configura tu hospital en minutos</span>
              </div>
            </div>
          </div>

          {/* Right - Interactive Dashboard Preview */}
          <div className="relative mt-8 lg:mt-0">
            {/* Main Dashboard Card */}
            <div className="relative bg-white rounded-2xl shadow-lg p-3 sm:p-6 border border-gray-200">
              {/* Main Dashboard - Real Interface */}
              <div className="bg-white rounded-xl overflow-hidden shadow-soft">
                <img
                  src="/images/hero-dashboard.png"
                  alt="Interfaz real de PreBedAI mostrando mapa de camas en tiempo real"
                  className="w-full h-auto object-cover object-top"
                />
              </div>

              {/* Simulador de estado de cama en tiempo real - Mobile */}
              <div className="sm:hidden absolute bottom-3 right-3 bg-white rounded-lg shadow-lg p-2.5 border-2 border-primary-500 z-10">
                <div className="text-center space-y-1.5">
                  <p className="text-[10px] text-gray-500 font-semibold">CAMA 204 - 3ª PLANTA</p>
                  <div className={`w-10 h-10 ${getBedStatusColor()} rounded-full mx-auto flex items-center justify-center transition-all duration-500`}>
                    <i className={`text-white text-lg ${
                      bedStatus === 'occupied' ? 'ri-user-line' :
                      bedStatus === 'pending' ? 'ri-loader-line animate-spin' :
                      'ri-checkbox-circle-line'
                    }`}></i>
                  </div>
                  <p className="text-[11px] font-bold text-gray-900">{getBedStatusText()}</p>
                  {bedStatus === 'pending' && (
                    <p className="text-[10px] text-amber-600">⏱ Limpieza en curso...</p>
                  )}
                  {bedStatus === 'available' && (
                    <p className="text-[10px] text-green-600">✓ Lista para asignar</p>
                  )}
                </div>
              </div>

              {/* Simulador de estado de cama en tiempo real - Desktop */}
              <div className="hidden sm:block absolute top-1/2 left-1/2 transform -translate-x-[10%] -translate-y-1/2 bg-white rounded-xl shadow-lg p-4 sm:p-6 border-2 border-primary-500">
                <div className="text-center space-y-2 sm:space-y-3">
                  <p className="text-xs text-gray-500 font-semibold">CAMA 204 - 3ª PLANTA</p>
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 ${getBedStatusColor()} rounded-full mx-auto flex items-center justify-center transition-all duration-500`}>
                    <i className={`text-white text-xl sm:text-2xl ${
                      bedStatus === 'occupied' ? 'ri-user-line' :
                      bedStatus === 'pending' ? 'ri-loader-line animate-spin' :
                      'ri-checkbox-circle-line'
                    }`}></i>
                  </div>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">{getBedStatusText()}</p>
                  {bedStatus === 'pending' && (
                    <p className="text-xs text-amber-600">⏱ Limpieza en curso...</p>
                  )}
                  {bedStatus === 'available' && (
                    <p className="text-xs text-green-600">✓ Lista para asignar</p>
                  )}
                </div>
              </div>

              {/* Floating Notification Cards - Mobile */}
              <div className="md:hidden absolute inset-0 pointer-events-none">
                {/* Card - Predicción IA */}
                <div className="absolute -bottom-3 -left-2 bg-white rounded-lg shadow-lg p-2 border-l-3 border-primary-500 z-10" style={{ animation: 'floatSimple 4s ease-in-out infinite', animationDelay: '1s' }}>
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 bg-primary-100 rounded flex items-center justify-center">
                      <i className="ri-brain-line text-primary-600 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-500">Predicción IA</p>
                      <p className="text-xs font-bold text-gray-900">85% alta hoy</p>
                    </div>
                  </div>
                </div>

                {/* Card - Disponible */}
                <div className="absolute -top-2 -right-2 bg-white rounded-lg shadow-lg p-2 border-l-3 border-green-500 z-10" style={{ animation: 'floatSimple 3s ease-in-out infinite' }}>
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-green-600 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-500">Cama 312</p>
                      <p className="text-xs font-bold text-green-600">✓ Disponible</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Notification Cards - Desktop */}
              <div className="hidden md:block absolute inset-0 pointer-events-none">
                {/* Card 1 - Top Right - Disponible */}
                <div className="absolute -top-3 -right-3 lg:-top-4 lg:-right-4 bg-white rounded-xl shadow-lg p-2 lg:p-4 border-l-4 border-green-500" style={{ animation: 'floatSimple 3s ease-in-out infinite' }}>
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-green-600 text-lg lg:text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Cama 312 - 4ª Planta</p>
                      <p className="text-xs lg:text-sm font-bold text-green-600">✓ Lista y disponible</p>
                    </div>
                  </div>
                </div>

                {/* Card 2 - Bottom Left - Predicción IA */}
                <div className="absolute -bottom-4 -left-4 lg:-bottom-6 lg:-left-6 bg-white rounded-xl shadow-medium p-2 lg:p-4 border-l-4 border-primary-500" style={{ animation: 'floatSimple 4s ease-in-out infinite', animationDelay: '1s' }}>
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <i className="ri-brain-line text-primary-600 text-lg lg:text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Predicción IA</p>
                      <p className="text-xs lg:text-sm font-bold text-gray-900">85% alta hoy - Hab. 218</p>
                    </div>
                  </div>
                </div>

                {/* Card 3 - Top Left - Alerta */}
                <div className="absolute -top-4 -left-6 lg:-top-6 lg:-left-8 bg-white rounded-xl shadow-medium p-2 lg:p-3 border-l-4 border-amber-500" style={{ animation: 'floatSimple 3.5s ease-in-out infinite', animationDelay: '0.5s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 lg:w-8 lg:h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                      <i className="ri-alert-line text-amber-600 text-sm lg:text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Limpieza pendiente</p>
                      <p className="text-xs font-bold text-gray-900">Cama 205</p>
                    </div>
                  </div>
                </div>

                {/* Card 4 - Bottom Right - Notificación */}
                <div className="absolute -bottom-3 -right-4 lg:-bottom-4 lg:-right-6 bg-white rounded-xl shadow-medium p-2 lg:p-3 border-l-4 border-primary-500" style={{ animation: 'floatSimple 3.2s ease-in-out infinite', animationDelay: '1.5s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 lg:w-8 lg:h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                      <i className="ri-notification-3-line text-primary-600 text-sm lg:text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Nuevo ingreso</p>
                      <p className="text-xs font-bold text-gray-900">Urgencias → 3ª</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && <VideoModal onClose={() => setShowVideo(false)} />}

      {/* Custom animations */}
      <style>{`
        @keyframes floatSimple {
          0%, 100% { 
            transform: translateY(0); 
          }
          50% { 
            transform: translateY(-10px); 
          }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </section>
  );
}
